import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Compass, Shuffle, Loader2, ArrowRight, RefreshCw,
  HelpCircle, Cog, Wand2, User, Clock, GitCompare, Link2, Sparkles, Zap, Brain,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const L = {
  fr: {
    eyebrow: "La Boussole de l'Inattendu", title: "Une question en crée une autre",
    subtitle: "Choisis un angle, laisse-toi surprendre par une question à laquelle tu n'aurais jamais pensé — puis creuse-la.",
    subjectPlaceholder: "Un sujet de départ (facultatif) : les arbres, le temps, la musique…",
    surprise: "Surprends-moi", generate: "Faire surgir une question", creuser: "Creuser cette question",
    another: "Une autre comme ça", loading: "La Boussole cherche le nord de l'inattendu…",
    error: "La Boussole s'est affolée. Réessaie.", pick: "Choisis un angle",
  },
  en: {
    eyebrow: "The Compass of the Unexpected", title: "One question sparks another",
    subtitle: "Pick an angle, let a question you'd never have thought of surprise you — then dig into it.",
    subjectPlaceholder: "A starting subject (optional): trees, time, music…",
    surprise: "Surprise me", generate: "Summon a question", creuser: "Dig into this question",
    another: "Another like this", loading: "The Compass is finding the north of the unexpected…",
    error: "The Compass spun out. Try again.", pick: "Pick an angle",
  },
  es: {
    eyebrow: "La Brújula de lo Inesperado", title: "Una pregunta crea otra",
    subtitle: "Elige un ángulo, déjate sorprender por una pregunta que nunca habrías imaginado — y luego profundiza.",
    subjectPlaceholder: "Un tema de partida (opcional): los árboles, el tiempo, la música…",
    surprise: "Sorpréndeme", generate: "Hacer surgir una pregunta", creuser: "Profundizar esta pregunta",
    another: "Otra así", loading: "La Brújula busca el norte de lo inesperado…",
    error: "La Brújula se descontroló. Inténtalo de nuevo.", pick: "Elige un ángulo",
  },
  nl: {
    eyebrow: "Het Kompas van het Onverwachte", title: "De ene vraag roept de andere op",
    subtitle: "Kies een invalshoek, laat je verrassen door een vraag waar je nooit aan gedacht zou hebben — en graaf dan door.",
    subjectPlaceholder: "Een startonderwerp (optioneel): bomen, tijd, muziek…",
    surprise: "Verras me", generate: "Een vraag oproepen", creuser: "Deze vraag uitdiepen",
    another: "Nog zo een", loading: "Het Kompas zoekt het noorden van het onverwachte…",
    error: "Het Kompas sloeg op hol. Probeer opnieuw.", pick: "Kies een invalshoek",
  },
};

const TYPES = [
  { key: "pourquoi", Icon: HelpCircle, fr: "Pourquoi", en: "Why", es: "Por qué", nl: "Waarom" },
  { key: "comment", Icon: Cog, fr: "Comment", en: "How", es: "Cómo", nl: "Hoe" },
  { key: "etsi", Icon: Wand2, fr: "Et si", en: "What if", es: "Y si", nl: "Wat als" },
  { key: "qui", Icon: User, fr: "Qui", en: "Who", es: "Quién", nl: "Wie" },
  { key: "quand", Icon: Clock, fr: "Quand", en: "When", es: "Cuándo", nl: "Wanneer" },
  { key: "comparer", Icon: GitCompare, fr: "Comparer", en: "Compare", es: "Comparar", nl: "Vergelijken" },
  { key: "relier", Icon: Link2, fr: "Relier", en: "Connect", es: "Relacionar", nl: "Verbinden" },
  { key: "imaginer", Icon: Sparkles, fr: "Imaginer", en: "Imagine", es: "Imaginar", nl: "Verbeelden" },
  { key: "challenger", Icon: Zap, fr: "Challenger", en: "Challenge", es: "Desafiar", nl: "Uitdagen" },
  { key: "philosopher", Icon: Brain, fr: "Philosopher", en: "Philosophise", es: "Filosofar", nl: "Filosoferen" },
];

export default function Boussole({ onExplore }) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const t = L[lang] || L.fr;
  const [subject, setSubject] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeType, setActiveType] = useState(null);
  const [result, setResult] = useState(null);

  const labelFor = (ty) => ty[lang] || ty.fr;

  const spark = async (type) => {
    setActiveType(type);
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/curiosity/spark", { subject: subject.trim(), type, lang });
      setResult(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || t.error);
    } finally {
      setLoading(false);
    }
  };

  const currentTypeLabel = () => {
    const ty = TYPES.find((x) => x.key === (result?.type || activeType));
    return ty ? labelFor(ty) : "";
  };

  return (
    <div className="h-full overflow-y-auto scroll-thin" data-testid="boussole">
      <div className="max-w-3xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-primary/80 uppercase tracking-[0.24em] text-xs mb-3">
            <Compass size={15} className="animate-[spin_9s_linear_infinite]" /> {t.eyebrow}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-gradient-orange mb-3">{t.title}</h1>
          <p className="text-foreground/55 leading-relaxed max-w-xl mx-auto">{t.subtitle}</p>
        </div>

        {/* Subject input */}
        <div className="glass rounded-2xl p-4 mb-6">
          <input
            data-testid="boussole-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && spark("surprise")}
            placeholder={t.subjectPlaceholder}
            maxLength={200}
            className="w-full bg-transparent outline-none text-foreground placeholder:text-foreground/30 text-lg py-2 px-2"
          />
        </div>

        {/* Surprise button */}
        <button
          data-testid="boussole-surprise"
          onClick={() => spark("surprise")}
          disabled={loading}
          className="btn-frost-orange w-full inline-flex items-center justify-center gap-2.5 px-8 py-4 rounded-full font-semibold text-base mb-6 disabled:opacity-50"
        >
          {loading && activeType === "surprise" ? <Loader2 size={18} className="animate-spin" /> : <Shuffle size={18} />}
          {t.surprise}
        </button>

        {/* Type grid */}
        <p className="text-xs uppercase tracking-[0.2em] text-foreground/40 mb-3 text-center">{t.pick}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-8" data-testid="boussole-types">
          {TYPES.map(({ key, Icon, ...labels }) => {
            const active = activeType === key && loading;
            return (
              <button
                key={key}
                data-testid={`boussole-type-${key}`}
                onClick={() => spark(key)}
                disabled={loading}
                className={`group flex items-center gap-2.5 rounded-xl border px-3.5 py-3 text-left transition-all disabled:opacity-50 ${
                  active
                    ? "border-primary bg-primary/15 text-primary"
                    : "border-white/10 bg-white/[0.02] text-foreground/70 hover:border-primary/40 hover:text-primary hover:bg-primary/[0.06]"
                }`}
              >
                {active ? <Loader2 size={16} className="animate-spin flex-shrink-0" /> : <Icon size={16} className="flex-shrink-0 text-primary/70 group-hover:text-primary" strokeWidth={1.7} />}
                <span className="text-sm font-medium truncate">{labels[lang] || labels.fr}</span>
              </button>
            );
          })}
        </div>

        {/* Result */}
        <AnimatePresence mode="wait">
          {loading && !result && (
            <motion.p
              key="loading"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="font-display italic text-foreground/45 text-lg text-center py-10 flex items-center justify-center gap-3"
              data-testid="boussole-loading"
            >
              <Loader2 className="animate-spin text-primary" size={20} /> {t.loading}
            </motion.p>
          )}

          {result && !loading && (
            <motion.div
              key={result.question}
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.5 }}
              className="glass frame-pulse rounded-3xl p-8 sm:p-10 text-center"
              data-testid="boussole-result"
            >
              <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-primary/70 mb-5">
                <Compass size={13} /> {currentTypeLabel()}
              </span>
              <p className="font-display italic text-gradient-orange text-2xl sm:text-3xl leading-snug mb-4" data-testid="boussole-question">
                « {result.question} »
              </p>
              {result.spark && (
                <p className="text-foreground/55 leading-relaxed mb-8" data-testid="boussole-spark">{result.spark}</p>
              )}
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  data-testid="boussole-creuser"
                  onClick={() => onExplore?.(result.question)}
                  className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold"
                >
                  {t.creuser} <ArrowRight size={17} />
                </button>
                <button
                  data-testid="boussole-another"
                  onClick={() => spark(result.type)}
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full font-medium border border-white/12 text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors"
                >
                  <RefreshCw size={15} /> {t.another}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
